// Follows a long-running backend operation (a certificate import, an efficiency test execution) by polling
// its progress, and tells the caller when it moves, ends, gets lost or takes too long.
//
// One module for every feature: what changes from one operation to another lives in POLLING_PROFILES, not
// in the callers. The server only answers "where is it now"; how often to ask is decided here, because the
// values are tuned once and rarely touched, and OKV has no usage peaks that would call for the server to
// steer them.

export const OPERATION_STATUS = {
  RUNNING: 'Running',
  SUCCEEDED: 'Succeeded',
  FAILED: 'Failed',
}

// baseMs      - wait between two polls while the operation moves.
// stepMs      - a step that is slow and shows nothing in between (the extraction waits on DOXA for tens of
//               seconds with no count to report) asks less often than the base.
// maxMs       - the longest wait, however long nothing changes or the server cannot be reached.
// timeoutMs   - when to stop following; a little over the longest the operation is allowed to take.
// untimedSteps - steps that wait on a shared resource for as long as it takes (the DOXA queue): while the
//               operation is in one of them the timeout does not apply, it is followed until it ends.
export const POLLING_PROFILES = {
  certificateImport: {
    baseMs: 2000,
    stepMs: { Extracting: 4000 },
    maxMs: 8000,
    timeoutMs: 12 * 60 * 1000,
  },
  efficiencyTestRun: {
    baseMs: 2000,
    stepMs: { WaitingResources: 4000 },
    maxMs: 10000,
    timeoutMs: 15 * 60 * 1000,
    untimedSteps: ['WaitingResources'],
  },
}

// Polls in a row that report the very same step and count before the wait starts to grow.
const UNCHANGED_POLLS_BEFORE_BACKOFF = 3
// What the wait is multiplied by for each poll that reports nothing new past that point...
const UNCHANGED_BACKOFF_FACTOR = 1.5
// ...and for each failed poll in a row.
const FAILURE_BACKOFF_FACTOR = 2

// The wait before the next poll.
//   progress   the last answer, or null when none has come yet (the very first poll, or after a failure)
//   unchanged  how many polls in a row have reported what the previous one did
//   failures   how many polls in a row have failed
export function nextDelay(profile, progress, unchanged = 0, failures = 0) {
  const base = (progress && profile.stepMs && profile.stepMs[progress.step]) || profile.baseMs
  const slowdown =
    Math.pow(UNCHANGED_BACKOFF_FACTOR, Math.max(0, unchanged - UNCHANGED_POLLS_BEFORE_BACKOFF)) *
    Math.pow(FAILURE_BACKOFF_FACTOR, failures)

  return Math.min(profile.maxMs, Math.round(base * slowdown))
}

export function isFinished(progress) {
  return !!progress && progress.status !== OPERATION_STATUS.RUNNING
}

function sameProgress(a, b) {
  return !!a && !!b && a.step === b.step && a.done === b.done && a.total === b.total && a.status === b.status
}

function httpStatusOf(error) {
  return error && error.response ? error.response.status : null
}

// Starts following an operation. Returns { stop }; call it when the page or component that follows goes
// away, or no longer cares, so that no poll outlives it.
//
//   profileName    a key of POLLING_PROFILES
//   fetchProgress  () => Promise of the progress { status, step, stepIndex, stepCount, done, total, errorCode }
//   handlers       onUpdate(progress)  every answer, the last one included
//                  onFinish(progress)  the operation ended, successfully or not (the last onUpdate comes first)
//                  onNotFound()        the server no longer has the operation (it was removed)
//                  onTimeout()         it did not end within the profile's timeout
//   env            { now, doc, setTimer, clearTimer } - replaceable for tests
//
// A poll that fails is retried, ever more slowly: the operation runs on the server whether or not this page
// can reach it, so a failed poll says nothing about it. Only the timeout ends the following.
// While the browser tab is hidden nothing is asked; the next poll happens when it is shown again.
export function startPolling(profileName, fetchProgress, handlers = {}, env = {}) {
  const profile = POLLING_PROFILES[profileName]
  if (!profile) {
    throw new Error('Unknown polling profile: ' + profileName)
  }

  const now = env.now || (() => Date.now())
  const doc = env.doc === undefined ? (typeof document === 'undefined' ? null : document) : env.doc
  const setTimer = env.setTimer || ((fn, ms) => setTimeout(fn, ms))
  const clearTimer = env.clearTimer || ((id) => clearTimeout(id))

  const startedAt = now()
  let stopped = false
  let timer = null
  let waitingForVisibility = false
  let last = null
  let unchanged = 0
  let failures = 0

  const isHidden = () => !!doc && doc.hidden === true

  function stop() {
    stopped = true
    if (timer !== null) {
      clearTimer(timer)
      timer = null
    }
    if (doc && doc.removeEventListener) {
      doc.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }

  function schedule(delay) {
    if (!stopped) {
      timer = setTimer(poll, delay)
    }
  }

  function onVisibilityChange() {
    if (waitingForVisibility && !isHidden() && !stopped) {
      waitingForVisibility = false
      poll()
    }
  }

  function poll() {
    timer = null
    if (stopped) {
      return
    }

    const untimed = !!last && !!profile.untimedSteps && profile.untimedSteps.includes(last.step)
    if (!untimed && now() - startedAt > profile.timeoutMs) {
      stop()
      if (handlers.onTimeout) {
        handlers.onTimeout()
      }
      return
    }

    if (isHidden()) {
      waitingForVisibility = true
      return
    }

    let request
    try {
      request = Promise.resolve(fetchProgress())
    } catch (error) {
      request = Promise.reject(error)
    }
    request.then(onAnswer, onFailure)
  }

  function onAnswer(progress) {
    if (stopped) {
      return
    }

    failures = 0
    unchanged = sameProgress(last, progress) ? unchanged + 1 : 0
    last = progress

    if (handlers.onUpdate) {
      handlers.onUpdate(progress)
    }

    if (isFinished(progress)) {
      stop()
      if (handlers.onFinish) {
        handlers.onFinish(progress)
      }
      return
    }

    schedule(nextDelay(profile, progress, unchanged, failures))
  }

  function onFailure(error) {
    if (stopped) {
      return
    }

    if (httpStatusOf(error) === 404) {
      stop()
      if (handlers.onNotFound) {
        handlers.onNotFound()
      }
      return
    }

    failures += 1
    schedule(nextDelay(profile, last, unchanged, failures))
  }

  if (doc && doc.addEventListener) {
    doc.addEventListener('visibilitychange', onVisibilityChange)
  }
  // The first poll is made at once: an operation that is quick should not be waited for.
  schedule(0)

  return { stop }
}
